import {
  OnlineOrderConfirmationState,
  OnlineOrderItemFragment,
  OnlineOrderState,
  OnlineOrderStateEventItemFragment,
} from '@client/graphql/types/graphql.ts';
import useFormData from '@client/module/utils/use-form-data.ts';
import Card from '@packages/ui/card/card.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import FormItem from '@packages/ui/form/form-item.tsx';
import { useFocusedCompany } from '@packages/core/company/focused-company-context.ts';
import CardHeader from '@packages/ui/card/card-header.tsx';
import DescriptionItem from '@packages/ui/description/description-item.tsx';
import moment from 'moment-timezone';
import { getFragmentData } from '@client/graphql/types';
import { OnlineOrderStateEventFragment } from '@client/graphql/online-order-fragment.ts';
import OnlineOrderActionConfirm from '@client/page/management/online-order/detail/component/online-order-action-confirm.tsx';
import OnlineOrderActionDecline from '@client/page/management/online-order/detail/component/online-order-action-decline.tsx';
import OnlineOrderActionCancel from '@client/page/management/online-order/detail/component/online-order-action-cancel.tsx';
import OnlineOrderActionDone from '@client/page/management/online-order/detail/component/online-order-action-done.tsx';
import OnlineOrderActionStartCooking from '@client/page/management/online-order/detail/component/online-order-action-start-cooking.tsx';
import CurrencyView from '@packages/core/utils/currency-view.tsx';
import { useTranslate } from '@tolgee/react';
import MenuSelectionInput from '@packages/ui/form/input/menu-selection-input.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import usePrintToKitchen from '@client/page/management/online-order/detail/logic/use-print-to-kitchen.ts';
import { TFnType } from '@tolgee/react';
import OnlineOrderTagEditor from '@client/page/management/online-order/tag/online-order-tag-editor.tsx';

const getOrderStateTranslation = (state: OnlineOrderState, t: TFnType): string => {
  switch (state) {
    case OnlineOrderState.WaitingForConfirmation:
      return t('online-order.dashboard.order-status.confirm', 'Waiting');
    case OnlineOrderState.InProgress:
      return t('online-order.dashboard.order-status.in-progress', 'Cooking');
    case OnlineOrderState.Done:
      return t('online-order.dashboard.order-status.completed', 'Completed');
    case OnlineOrderState.Canceled:
      return t('online-order.dashboard.order-status.cancelled', 'Cancelled');
    case OnlineOrderState.WaitingToCook:
      return t('online-order.dashboard.order-status.waiting-to-cook', 'Waiting to cook');
    default:
      return t('online-order.dashboard.order-status.unknown', 'Unknown');
  }
};

const preparingTimeBlock = [10, 15, 20, 30, 40, 45, 60, 90, 105, 120];

interface Form {
  preparingTime?: number | null;
  deliveryFee: number;
}

const OnlineOrderAction = (props: { order: OnlineOrderItemFragment; compact?: boolean; onClose?: () => void }) => {
  const compact = props.compact ?? false;
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();
  const { order, onClose } = props;
  // Hooks
  // const currency = useCompanyCurrency();
  // const [updateDeliveryPrice] = useChangeDeliveryPrice();
  // const { showAlert } = useNotificationCenter();
  const { data, manualHandle } = useFormData<Form>({
    preparingTime: order.estimatedDuration ?? 0,
    deliveryFee: order.deliveryPrice ?? 0,
  });

  const [printToKitchen] = usePrintToKitchen();

  const events = order.events.map((e) => getFragmentData(OnlineOrderStateEventFragment, e));

  // const handleDeliveryPrice = useCallback(
  //   (deliveryFee: number) => {
  //     updateDeliveryPrice({
  //       variables: {
  //         id: order.id,
  //         deliveryPrice: deliveryFee,
  //       },
  //     })
  //       .then(() => {
  //         showAlert({
  //           status: 'success',
  //           title: 'Success',
  //           message: 'Your online order delivery fee is update',
  //         });
  //       })
  //       .catch((e: Error) => {
  //         showAlert({
  //           status: 'error',
  //           title: 'Error',
  //           message: e.message,
  //         });
  //       });
  //   },
  //   [order.id, showAlert, updateDeliveryPrice]
  // );

  const handlePrintAction = async () => {
    await printToKitchen({ variables: { id: props.order.id } });
    showAlert({
      status: 'success',
      title: t('common.success', 'Success'),
      message: t('online-order.print.alert.success', 'Order detail has been printed'),
    });
  };
  const display = (
    <>
      {!compact && (
        <DescriptionItem title={t('online-order.dashboard.order-status.dialog.amount')}>
          <CurrencyView price={order.totalPrice} />
        </DescriptionItem>
      )}
      <DescriptionItem title={t('online-order.dashboard.order-status.dialog.order-time', 'Order time')}>
        <PreparingTime events={events} order={order} />
      </DescriptionItem>
      <DescriptionItem
        title={t('online-order.dashboard.order-status.dialog.estimate-time', 'Estimated completion time')}
      >
        <EstimatedCompletionTime events={events} preparingTime={data.preparingTime} order={order} />
      </DescriptionItem>
    </>
  );

  const form = (
    <FormLayout>
      <FormItem title={t('online-order.dashboard.order-status.dialog.prepare-time-in-minute')} className="xl:max-w-md">
        <MenuSelectionInput
          title={''}
          data={preparingTimeBlock}
          value={data.preparingTime ?? 30}
          onChange={(value) => {
            if (
              order.confirmationState === OnlineOrderConfirmationState.Pending ||
              order.state === OnlineOrderState.WaitingToCook
            ) {
              manualHandle('preparingTime', value);
            }
          }}
          build={(item) => {
            return {
              id: item.toString(),
              name: item.toString(),
            };
          }}
          className="max-w-sm mb-2"
        />
      </FormItem>
    </FormLayout>
  );

  const proofForConfirmation =
    order.manualTransferProofSubmittedAt && !order.manualTransferConfirmedAt
      ? {
          // Only attach the image when rendered outside the detail page (where the proof
          // is already visible in the manual transfer panel).
          manualTransferProofImageOrigin: compact ? null : order.manualTransferProofImageOrigin,
          manualTransferProofImageThumbnail: compact ? null : order.manualTransferProofImageThumbnail,
          manualTransferSenderName: order.manualTransferSenderName,
        }
      : null;

  const pendingActions = (
    <>
      <OnlineOrderActionDecline orderID={order.id} onClose={props.onClose} />
      <div className="flex-grow" />
      <OnlineOrderActionConfirm
        orderID={order.id}
        preparingTime={data.preparingTime}
        onClose={props.onClose}
        proof={proofForConfirmation}
      />
    </>
  );

  const inProgressActions = (
    <>
      <OnlineOrderActionCancel orderID={order.id} onClose={props.onClose} />
      <div className="flex-grow" />
      <OnlineOrderActionDone orderID={order.id} onClose={props.onClose} />
    </>
  );

  const waitingToCookActions = (
    <>
      <OnlineOrderActionCancel orderID={order.id} onClose={props.onClose} />
      <div className="flex-grow" />
      <OnlineOrderActionStartCooking orderID={order.id} preparingTime={data.preparingTime} onClose={props.onClose} />
    </>
  );

  return (
    <Card>
      <CardHeader
        title={t('online-order.dashboard.order-status.dialog.title')}
        onCrossButton={
          onClose &&
          (() => {
            onClose();
          })
        }
      >
        <SecondaryButton
          onClick={() => {
            void handlePrintAction();
          }}
        >
          {t('online-order.detail.print-button', 'Print order detail')}
        </SecondaryButton>
      </CardHeader>
      <CardContent>
        {!compact && (
          <div className="bg-gray-50 px-3 py-1.5 rounded-lg flex items-start mb-4 border border-gray-100 shadow-sm">
            <DescriptionItem title={t('online-order.dashboard.order-status.dialog.status')}>
              {getOrderStateTranslation(order.state, t)}
            </DescriptionItem>
          </div>
        )}
        {(order.confirmationState === OnlineOrderConfirmationState.Pending ||
          order.state === OnlineOrderState.WaitingToCook) &&
        !order.expectedTime
          ? form
          : display}

        <div className="mt-3">
          <div className="text-xs font-medium text-gray-500 mb-1">
            {t('online-order.tags.label', 'Tags')}
          </div>
          <OnlineOrderTagEditor orderID={order.id} tags={order.tags} />
        </div>
      </CardContent>

      {order.state !== OnlineOrderState.Done && order.state !== OnlineOrderState.Canceled && (
        <CardFooter>
          {order.confirmationState === OnlineOrderConfirmationState.Pending && pendingActions}
          {order.state === OnlineOrderState.WaitingToCook && waitingToCookActions}
          {order.state === OnlineOrderState.InProgress && inProgressActions}
        </CardFooter>
      )}
    </Card>
  );
};

const PreparingTime = (props: {
  events: readonly OnlineOrderStateEventItemFragment[];
  order: OnlineOrderItemFragment;
}) => {
  const { settings } = useFocusedCompany();

  if (!props.order.expectedTime) {
    const waitingForConfirmationEvent = props.events.find((event) => event.state === OnlineOrderState.InProgress);

    if (waitingForConfirmationEvent) {
      return <>{moment(waitingForConfirmationEvent.timestamp).tz(settings.timezone).format('HH:mm')}</>;
    }

    return;
  }

  return moment(props.order.expectedTime).tz(settings.timezone).format('HH:mm');
};

const EstimatedCompletionTime = (props: {
  order: OnlineOrderItemFragment;
  events: readonly OnlineOrderStateEventItemFragment[];
  preparingTime?: number | null;
}) => {
  const { settings } = useFocusedCompany();

  if (props.preparingTime == null) {
    return null;
  }

  if (!props.order.expectedTime) {
    const inProgressEvent = props.events.find((event) => event.state === OnlineOrderState.InProgress);

    if (inProgressEvent) {
      return (
        <>
          {moment(inProgressEvent.timestamp).add(props.preparingTime, 'minutes').tz(settings.timezone).format('HH:mm')}
        </>
      );
    }
    return;
  }

  return (
    <>{moment(props.order.expectedTime).add(props.preparingTime, 'minutes').tz(settings.timezone).format('HH:mm')}</>
  );
};

export default OnlineOrderAction;
